import {
  ChevronRight,
  Folder,
  FolderOpen,
  FolderPlus,
  Lock,
  MoreVertical,
  Pencil,
  Trash2,
} from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { DocumentFolder } from "@/features/documents/types";
import { cn } from "@/lib/utils";

export interface FolderActions {
  onSelect: (folderId: string | null) => void;
  onNewSubfolder: (parent: DocumentFolder) => void;
  onRename: (folder: DocumentFolder) => void;
  onDelete: (folder: DocumentFolder) => void;
}

interface FolderTreeProps extends FolderActions {
  folders: DocumentFolder[];
  selectedFolderId: string | null;
  /** Filter term for the sidebar search box (matches folder names). */
  filter?: string;
}

/** Recursive, N-level folder tree with per-folder actions and system-folder locks. */
export function FolderTree({
  folders,
  selectedFolderId,
  filter,
  onSelect,
  onNewSubfolder,
  onRename,
  onDelete,
}: FolderTreeProps) {
  const childrenOf = useMemo(() => {
    const map = new Map<string | null, DocumentFolder[]>();
    for (const folder of folders) {
      const key = folder.parent;
      const list = map.get(key) ?? [];
      list.push(folder);
      map.set(key, list);
    }
    return map;
  }, [folders]);

  const term = (filter ?? "").trim().toLowerCase();
  // When searching, flatten to any folder whose name matches (ignore nesting).
  if (term) {
    const matches = folders.filter((f) => f.name.toLowerCase().includes(term));
    return (
      <div className="flex flex-col gap-1">
        {matches.map((folder) => (
          <FolderRow
            key={folder.id}
            folder={folder}
            depth={0}
            selected={selectedFolderId === folder.id}
            expandable={false}
            expanded={false}
            onToggle={() => {}}
            onSelect={onSelect}
            onNewSubfolder={onNewSubfolder}
            onRename={onRename}
            onDelete={onDelete}
          />
        ))}
        {matches.length === 0 && (
          <p className="text-muted-foreground px-3 py-2 text-sm">No folders match.</p>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <TreeLevel
        parentId={null}
        depth={0}
        childrenOf={childrenOf}
        selectedFolderId={selectedFolderId}
        onSelect={onSelect}
        onNewSubfolder={onNewSubfolder}
        onRename={onRename}
        onDelete={onDelete}
      />
    </div>
  );
}

interface TreeLevelProps extends FolderActions {
  parentId: string | null;
  depth: number;
  childrenOf: Map<string | null, DocumentFolder[]>;
  selectedFolderId: string | null;
}

function TreeLevel({
  parentId,
  depth,
  childrenOf,
  selectedFolderId,
  ...actions
}: TreeLevelProps) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const nodes = childrenOf.get(parentId) ?? [];
  return (
    <>
      {nodes.map((folder) => {
        const kids = childrenOf.get(folder.id) ?? [];
        const isCollapsed = collapsed.has(folder.id);
        return (
          <Fragment key={folder.id}>
            <FolderRow
              folder={folder}
              depth={depth}
              selected={selectedFolderId === folder.id}
              expandable={kids.length > 0}
              expanded={!isCollapsed}
              onToggle={() =>
                setCollapsed((prev) => {
                  const next = new Set(prev);
                  next.has(folder.id) ? next.delete(folder.id) : next.add(folder.id);
                  return next;
                })
              }
              {...actions}
            />
            {kids.length > 0 && !isCollapsed && (
              <TreeLevel
                parentId={folder.id}
                depth={depth + 1}
                childrenOf={childrenOf}
                selectedFolderId={selectedFolderId}
                {...actions}
              />
            )}
          </Fragment>
        );
      })}
    </>
  );
}

interface FolderRowProps extends FolderActions {
  folder: DocumentFolder;
  depth: number;
  selected: boolean;
  expandable: boolean;
  expanded: boolean;
  onToggle: () => void;
}

function FolderRow({
  folder,
  depth,
  selected,
  expandable,
  expanded,
  onToggle,
  onSelect,
  onNewSubfolder,
  onRename,
  onDelete,
}: FolderRowProps) {
  return (
    <div
      className={cn(
        "group/folder flex items-center gap-1 rounded-lg pr-1",
        selected ? "bg-primary/10 text-primary" : "text-foreground hover:bg-muted",
      )}
      style={{ paddingLeft: `${depth * 16}px` }}
    >
      <button
        type="button"
        aria-label={expanded ? "Collapse folder" : "Expand folder"}
        className={cn("flex size-6 items-center justify-center", !expandable && "invisible")}
        onClick={onToggle}
      >
        <ChevronRight className={cn("size-4 transition-transform", expanded && "rotate-90")} />
      </button>
      <button
        type="button"
        onClick={() => onSelect(folder.id)}
        className="flex flex-1 items-center gap-2 py-2 text-left text-sm font-medium"
      >
        {selected ? (
          <FolderOpen className="size-4 shrink-0" />
        ) : (
          <Folder className="size-4 shrink-0 opacity-70" />
        )}
        <span className="truncate">{folder.name}</span>
        {folder.isSystem && (
          <Lock className="text-muted-foreground size-3 shrink-0" aria-label="System folder" />
        )}
        {folder.documentCount > 0 && (
          <span className="text-muted-foreground ml-auto text-xs">{folder.documentCount}</span>
        )}
      </button>
      {!folder.isSystem && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={`Actions for ${folder.name}`}
              className="opacity-0 group-hover/folder:opacity-100 data-[state=open]:opacity-100"
            >
              <MoreVertical className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onNewSubfolder(folder)}>
              <FolderPlus className="size-4" /> New subfolder
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onRename(folder)}>
              <Pencil className="size-4" /> Rename
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() => onDelete(folder)}
            >
              <Trash2 className="size-4" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
